<template>
  <div v-if="!isAdminRoute" class="support-widget" :class="{ 'is-open': open }">
    <transition name="support-teaser">
      <button v-if="teaserVisible && !open" class="support-teaser" type="button" @click="openChat">
        <strong>Помочь с выбором?</strong>
        <span>Виртуальный помощник уже здесь</span>
      </button>
    </transition>

    <transition name="support-panel">
      <section v-if="open" class="support-panel" role="dialog" aria-modal="false" aria-label="Чат поддержки">
        <header class="support-head">
          <div class="support-avatar" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M5.5 11V8.8A2.8 2.8 0 0 1 8.3 6h7.4a2.8 2.8 0 0 1 2.8 2.8V11"/><path d="M4 10.5a2 2 0 0 0-2 2V17h20v-4.5a2 2 0 0 0-4 0V14H6v-1.5a2 2 0 0 0-2-2Z"/><path d="M5 17v2M19 17v2"/></svg>
          </div>
          <div>
            <strong>Помощник «ДРУЖБЫ»</strong>
            <span><i></i> Бот отвечает сразу · менеджер подключится</span>
          </div>
          <button type="button" aria-label="Закрыть чат" @click="closeChat">×</button>
        </header>

        <div ref="messages" class="support-messages" aria-live="polite">
          <div v-if="loading && !messages.length" class="support-loading">Подключаем чат…</div>
          <article v-for="message in messages" :key="message.id" :class="['support-message', `is-${message.sender}`]">
            <div>
              <small>{{ senderLabel(message.sender) }}</small>
              <p>{{ message.message }}</p>
              <time>{{ formatTime(message.created_at) }}</time>
            </div>
          </article>
          <div v-if="sending" class="support-typing"><i></i><i></i><i></i></div>
          <p v-if="error" class="support-error">{{ error }}</p>
        </div>

        <div v-if="suggestions.length && !contactOpen" class="support-suggestions">
          <button v-for="item in suggestions" :key="item" type="button" @click="useSuggestion(item)">{{ item }}</button>
        </div>

        <form v-if="contactOpen" class="support-contact" @submit.prevent="saveContact">
          <div class="support-contact__head">
            <strong>Как с вами связаться?</strong>
            <button type="button" @click="contactOpen = false">×</button>
          </div>
          <input v-model.trim="contact.name" type="text" maxlength="120" autocomplete="name" placeholder="Ваше имя">
          <input v-model.trim="contact.phone" type="tel" maxlength="50" autocomplete="tel" placeholder="Телефон">
          <input v-model.trim="contact.email" type="email" maxlength="190" autocomplete="email" placeholder="Электронная почта">
          <button type="submit" :disabled="contactSaving || !privacyAccepted">{{ contactSaving ? 'Сохраняем…' : 'Передать менеджеру' }}</button>
        </form>

        <label class="support-legal"><input v-model="privacyAccepted" type="checkbox" @change="savePrivacyChoice"><span>Даю <button type="button" @click="openLegal('/personal-data-consent')">согласие на обработку данных</button>. <button type="button" @click="openLegal('/privacy')">Политика</button>.</span></label>

        <form class="support-compose" @submit.prevent="send">
          <textarea v-model="draft" rows="1" maxlength="1000" placeholder="Напишите сообщение…" @keydown.enter.exact.prevent="send"></textarea>
          <button type="submit" :disabled="sending || !draft.trim() || !privacyAccepted" aria-label="Отправить">
            <svg viewBox="0 0 24 24"><path d="m4 4 17 8-17 8 3-8-3-8Z"/><path d="M7 12h14"/></svg>
          </button>
        </form>
        <footer>Виртуальный помощник. Сообщения видит менеджер фабрики.</footer>
      </section>
    </transition>

    <button class="support-launcher" type="button" :aria-label="open ? 'Закрыть чат' : 'Открыть чат поддержки'" @click="toggle">
      <svg v-if="!open" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-5 4v-4H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"/><path d="M8 10h8M8 13h5"/></svg>
      <span v-else>×</span>
      <b v-if="unread && !open">{{ unread > 9 ? '9+' : unread }}</b>
    </button>
  </div>
</template>

<script>
import axios from 'axios'
import { trackGoal, trackGoalOnce, trackError } from '@/analytics/metrica'
import { LEGAL_DOCUMENT_VERSIONS, logLegalConsent } from '@/legal/consent'

const API = 'https://sitebypro-server.ru/druzbamebel/support'
const SESSION_KEY = 'druzhba_support_session'
const TOKEN_KEY = 'druzhba_support_token'
const SEEN_KEY = 'druzhba_support_last_seen'
const TEASER_KEY = 'druzhba_support_teaser_closed'

export default {
  name: 'SupportChatWidget',
  data() {
    return {
      open: false,
      loading: false,
      polling: false,
      sending: false,
      error: '',
      messages: [],
      suggestions: [],
      draft: '',
      sessionId: '',
      sessionToken: '',
      latestId: 0,
      lastSeenId: 0,
      unread: 0,
      pollTimer: null,
      teaserTimer: null,
      teaserVisible: false,
      contactOpen: false,
      contactSaving: false,
      contact: { name: '', phone: '', email: '' },
      privacyAccepted: false,
      previousOverflow: ''
    }
  },
  computed: {
    isAdminRoute() { return this.$route.path.startsWith('/admin') }
  },
  watch: {
    '$route.path'() { if (this.isAdminRoute) this.closeChat() }
  },
  mounted() {
    this.restoreSession()
    try { this.privacyAccepted = localStorage.getItem('druzhba_support_pd_consent_v1') === LEGAL_DOCUMENT_VERSIONS.personalData } catch (_) {}
    if (this.sessionId && this.sessionToken) {
      this.bootstrap(false)
      this.startPolling()
    }
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    clearInterval(this.pollTimer)
    clearTimeout(this.teaserTimer)
    document.body.style.overflow = this.previousOverflow || ''
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    openLegal(path) { const routeData = this.$router.resolve(path); window.open(routeData.href, '_blank', 'noopener,noreferrer') },
    savePrivacyChoice() { try { if (this.privacyAccepted) localStorage.setItem('druzhba_support_pd_consent_v1', LEGAL_DOCUMENT_VERSIONS.personalData); else localStorage.removeItem('druzhba_support_pd_consent_v1') } catch (_) {}; logLegalConsent('personal_data_support', this.privacyAccepted, { document_version: LEGAL_DOCUMENT_VERSIONS.personalData }) },
    restoreSession() {
      try {
        this.sessionId = localStorage.getItem(SESSION_KEY) || ''
        this.sessionToken = localStorage.getItem(TOKEN_KEY) || ''
        this.lastSeenId = Math.max(0, Number(localStorage.getItem(SEEN_KEY)) || 0)
      } catch (_) {}
    },
    saveSession() {
      try {
        localStorage.setItem(SESSION_KEY, this.sessionId)
        localStorage.setItem(TOKEN_KEY, this.sessionToken)
      } catch (_) {}
    },
    scheduleTeaser() {
      try { if (localStorage.getItem(TEASER_KEY)) return } catch (_) {}
      return false
    },
    toggle() { this.open ? this.closeChat() : this.openChat() },
    openChat() {
      this.open = true
      trackGoal('chat_open')
      this.teaserVisible = false
      try { localStorage.setItem(TEASER_KEY, '1') } catch (_) {}
      this.previousOverflow = document.body.style.overflow
      if (window.innerWidth <= 680) document.body.style.overflow = 'hidden'
      this.unread = 0
      if (!this.sessionId || !this.sessionToken) this.bootstrap(false)
      else this.bootstrap(true)
      this.markRead()
      this.startPolling()
      this.$nextTick(this.scrollBottom)
    },
    closeChat() {
      this.open = false
      trackGoal('chat_close')
      this.contactOpen = false
      document.body.style.overflow = this.previousOverflow || ''
      clearInterval(this.pollTimer)
      this.pollTimer = null
      if (!this.isAdminRoute && this.sessionId && this.sessionToken) this.startPolling()
    },
    onKeydown(event) { if (event.key === 'Escape' && this.open) this.closeChat() },
    dismissTeaser() {
      this.teaserVisible = false
      try { localStorage.setItem(TEASER_KEY, '1') } catch (_) {}
    },
    startPolling() {
      clearInterval(this.pollTimer)
      if (!this.sessionId || !this.sessionToken) return
      this.pollTimer = setInterval(() => this.bootstrap(true), this.open ? 4000 : 12000)
    },
    async bootstrap(silent) {
      if (this.loading || this.polling || this.isAdminRoute) return
      if (silent) this.polling = true
      else this.loading = true
      try {
        const response = await axios.post(`${API}/bootstrap`, {
          session_id: this.sessionId,
          session_token: this.sessionToken,
          after_id: this.latestId,
          source_url: window.location.href
        }, { timeout: 12000 })
        const payload = response && response.data && response.data.data
        if (String(response && response.data && response.data.status) !== '200' || !payload) throw new Error('Чат недоступен')
        this.sessionId = payload.session_id
        this.sessionToken = payload.session_token
        this.saveSession()
        if (!this.pollTimer) this.startPolling()
        this.appendMessages(payload.messages || [])
        if (!this.suggestions.length) this.suggestions = payload.suggestions || []
        this.error = ''
        if (this.open) this.markRead()
      } catch (error) {
        if (!silent) this.error = 'Не удалось подключиться к чату. Проверьте интернет и попробуйте ещё раз.'
        if (!silent) { trackGoal('chat_error', { stage: 'bootstrap' }); trackError('support-chat-bootstrap', error) }
      } finally {
        if (silent) this.polling = false
        else this.loading = false
      }
    },
    appendMessages(items) {
      const known = new Set(this.messages.map(item => Number(item.id)))
      let newAdmin = 0
      items.forEach(item => {
        if (!known.has(Number(item.id))) {
          this.messages.push(item)
          if (item.sender === 'bot') trackGoal('chat_bot_reply')
          if (item.sender === 'admin') trackGoal('chat_admin_reply')
          if (item.sender === 'admin' && !this.open && Number(item.id) > this.lastSeenId) newAdmin += 1
        }
        this.latestId = Math.max(this.latestId, Number(item.id) || 0)
      })
      if (newAdmin) this.unread += newAdmin
      if (items.length) this.$nextTick(this.scrollBottom)
    },
    async send() {
      const message = this.draft.trim()
      if (message) trackGoalOnce('chat_form_start', this.sessionId || 'session')
      if (!message || this.sending) return
      if (!this.privacyAccepted) { this.error = 'Подтвердите согласие на обработку персональных данных.'; return }
      this.sending = true
      trackGoal('chat_message_send', { chat: { length: message.length } })
      this.error = ''
      this.draft = ''
      try {
        const response = await axios.post(`${API}/send`, {
          session_id: this.sessionId,
          session_token: this.sessionToken,
          message,
          source_url: window.location.href
        }, { timeout: 15000 })
        const payload = response && response.data && response.data.data
        if (String(response && response.data && response.data.status) !== '200' || !payload) throw new Error(payload && payload.text || 'Ошибка отправки')
        this.appendMessages(payload.messages || [])
        this.suggestions = payload.suggestions || []
        if (payload.contact_requested) { this.contactOpen = true; trackGoal('chat_manager_request') }
        this.markRead()
      } catch (error) {
        this.draft = message
        const payload = error && error.response && error.response.data && error.response.data.data
        this.error = payload && payload.text ? payload.text : 'Сообщение не отправлено. Попробуйте ещё раз.'
        trackGoal('chat_error', { stage: 'send' })
        trackError('support-chat-send', error)
      } finally {
        this.sending = false
      }
    },
    useSuggestion(item) {
      if (item === 'Оставить контакты' || item === 'Позвать менеджера') {
        if (item === 'Оставить контакты') { this.contactOpen = true; return }
      }
      if (item === 'Перейти в каталог') { this.$router.push('/catalog'); this.closeChat(); return }
      if (item === 'Открыть профиль') { this.$router.push('/profile'); this.closeChat(); return }
      this.draft = item
      this.send()
    },
    async saveContact() {
      if (this.contactSaving) return
      if (!this.contact.phone && !this.contact.email) {
        this.error = 'Укажите телефон или электронную почту.'
        return
      }
      if (!this.privacyAccepted) { this.error = 'Подтвердите согласие на обработку персональных данных.'; return }
      this.contactSaving = true
      this.error = ''
      try {
        const response = await axios.post(`${API}/contact`, {
          session_id: this.sessionId,
          session_token: this.sessionToken,
          ...this.contact
        }, { timeout: 12000 })
        const payload = response && response.data && response.data.data
        if (String(response && response.data && response.data.status) !== '200' || !payload) throw new Error('Не удалось сохранить контакты')
        if (payload.message) this.appendMessages([payload.message])
        this.contactOpen = false
        trackGoal('chat_contact_submit')
        this.suggestions = ['Подобрать диван', 'Доставка', 'Где мой заказ?']
      } catch (error) {
        const payload = error && error.response && error.response.data && error.response.data.data
        this.error = payload && payload.text ? payload.text : 'Не удалось сохранить контакты.'
        trackGoal('chat_error', { stage: 'contact' })
        trackError('support-chat-contact', error)
      } finally { this.contactSaving = false }
    },
    async markRead() {
      if (!this.open || !this.latestId || !this.sessionId) return
      try {
        await axios.post(`${API}/read`, {
          session_id: this.sessionId,
          session_token: this.sessionToken,
          message_id: this.latestId
        }, { timeout: 8000 })
        this.lastSeenId = this.latestId
        try { localStorage.setItem(SEEN_KEY, String(this.latestId)) } catch (_) {}
      } catch (_) {}
    },
    senderLabel(sender) {
      return { visitor: 'Вы', admin: 'Менеджер', bot: 'Виртуальный помощник', system: 'Система' }[sender] || ''
    },
    formatTime(value) {
      if (!value) return ''
      const date = new Date(String(value).replace(' ', 'T'))
      return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    },
    scrollBottom() {
      const node = this.$refs.messages
      if (node) node.scrollTop = node.scrollHeight
    }
  }
}
</script>

<style scoped>
.support-widget{position:fixed;z-index:950;right:22px;bottom:22px;color:#352d27;text-align:left}.support-launcher{position:relative;display:grid;width:58px;height:58px;place-items:center;border:0;border-radius:50%;background:#b07c50;color:#fff;box-shadow:0 14px 34px rgba(80,51,29,.3);cursor:pointer}.support-launcher:hover{background:#8f603b;transform:translateY(-2px)}.support-launcher svg{width:27px;height:27px;fill:none;stroke:currentColor;stroke-width:1.7;stroke-linecap:round;stroke-linejoin:round}.support-launcher>span{font-size:28px;font-weight:300}.support-launcher b{position:absolute;top:-3px;right:-2px;display:grid;min-width:20px;height:20px;padding:0 5px;place-items:center;border:2px solid #fff;border-radius:999px;background:#cf4f42;color:#fff;font-size:9px}.support-teaser{position:absolute;right:0;bottom:70px;width:230px;padding:15px 38px 15px 16px;border:1px solid #eadfd5;border-radius:16px;background:#fff;box-shadow:0 18px 45px rgba(53,39,29,.18);color:#382f29;text-align:left;cursor:pointer}.support-teaser strong,.support-teaser span{display:block}.support-teaser strong{font-size:13px}.support-teaser span{margin-top:4px;color:#7c6f66;font-size:10px}.support-panel{position:absolute;right:0;bottom:72px;display:flex;width:390px;height:min(620px,calc(100vh - 125px));overflow:hidden;flex-direction:column;border:1px solid #e4d7cc;border-radius:22px;background:#fff;box-shadow:0 22px 65px rgba(54,37,25,.25)}.support-head{display:grid;grid-template-columns:42px minmax(0,1fr) 32px;align-items:center;gap:10px;padding:15px 16px;background:#302821;color:#fff}.support-avatar{display:grid;width:42px;height:42px;place-items:center;border-radius:13px;background:#b07c50}.support-avatar svg{width:24px;height:24px;fill:none;stroke:#fff;stroke-width:1.6;stroke-linecap:round;stroke-linejoin:round}.support-head strong,.support-head span{display:block}.support-head strong{font-size:13px}.support-head span{margin-top:4px;color:#d5c8be;font-size:8px}.support-head span i{display:inline-block;width:6px;height:6px;margin-right:3px;border-radius:50%;background:#71c27b}.support-head>button{width:32px;height:32px;border:0;border-radius:9px;background:rgba(255,255,255,.08);color:#fff;font-size:20px;cursor:pointer}.support-messages{flex:1;overflow-y:auto;padding:18px 14px;background:#f8f4f0;scroll-behavior:smooth}.support-message{display:flex;margin-bottom:12px}.support-message>div{max-width:84%;padding:10px 12px;border-radius:14px;background:#fff;box-shadow:0 3px 12px rgba(70,50,36,.06)}.support-message small{display:block;margin-bottom:5px;color:#9a887a;font-size:8px;font-weight:700}.support-message p{margin:0;white-space:pre-wrap;font-size:11px;line-height:1.55}.support-message time{display:block;margin-top:5px;color:#a99d94;font-size:7px;text-align:right}.support-message.is-visitor{justify-content:flex-end}.support-message.is-visitor>div{border-bottom-right-radius:4px;background:#b07c50;color:#fff}.support-message.is-visitor small,.support-message.is-visitor time{color:rgba(255,255,255,.72)}.support-message.is-bot>div{border-bottom-left-radius:4px}.support-message.is-admin>div{border:1px solid #d8c0aa;border-bottom-left-radius:4px;background:#fffaf5}.support-message.is-admin small{color:#8f603b}.support-message.is-system{justify-content:center}.support-message.is-system>div{max-width:95%;padding:7px 10px;background:#ece5de;color:#766a61;text-align:center;box-shadow:none}.support-message.is-system small,.support-message.is-system time{display:none}.support-typing{display:flex;width:max-content;gap:4px;padding:11px 13px;border-radius:13px;background:#fff}.support-typing i{width:5px;height:5px;border-radius:50%;background:#ad9f95;animation:support-dot 1s infinite}.support-typing i:nth-child(2){animation-delay:.15s}.support-typing i:nth-child(3){animation-delay:.3s}.support-loading,.support-error{padding:12px;border-radius:10px;font-size:9px}.support-loading{background:#eee7e0;color:#766b62}.support-error{margin:10px 0 0;background:#fff0ed;color:#994238}.support-suggestions{display:flex;gap:7px;overflow-x:auto;padding:9px 12px;border-top:1px solid #eee5de;background:#fff;scrollbar-width:none}.support-suggestions::-webkit-scrollbar{display:none}.support-suggestions button{flex:0 0 auto;height:30px;padding:0 10px;border:1px solid #dcc6b3;border-radius:999px;background:#fff9f4;color:#805436;font-size:8px;font-weight:700;white-space:nowrap;cursor:pointer}.support-contact{display:grid;gap:7px;padding:12px;border-top:1px solid #eadfd5;background:#fffaf6}.support-contact__head{display:flex;align-items:center;justify-content:space-between}.support-contact__head strong{font-size:10px}.support-contact__head button{border:0;background:transparent;color:#8c7f75;font-size:16px}.support-contact input{width:100%;height:36px;padding:0 10px;border:1px solid #e0d4ca;border-radius:9px;outline:0;font-size:10px}.support-contact input:focus{border-color:#b07c50}.support-contact>button{height:38px;border:0;border-radius:9px;background:#b07c50;color:#fff;font-size:9px;font-weight:800}.support-compose{display:grid;grid-template-columns:minmax(0,1fr) 42px;align-items:end;gap:8px;padding:11px 12px;border-top:1px solid #eadfd5;background:#fff}.support-compose textarea{width:100%;max-height:88px;min-height:42px;resize:none;padding:12px;border:1px solid #ded1c6;border-radius:12px;outline:0;color:#3d342e;font:inherit;font-size:10px;line-height:1.45}.support-compose textarea:focus{border-color:#b07c50}.support-compose button{display:grid;width:42px;height:42px;place-items:center;border:0;border-radius:12px;background:#b07c50;color:#fff;cursor:pointer}.support-compose button:disabled{opacity:.45;cursor:not-allowed}.support-compose svg{width:20px;height:20px;fill:none;stroke:currentColor;stroke-width:1.7;stroke-linecap:round;stroke-linejoin:round}.support-panel footer{padding:0 12px 10px;background:#fff;color:#a09389;font-size:7px;text-align:center}.support-panel-enter-active,.support-panel-leave-active{transition:opacity .2s ease,transform .22s ease}.support-panel-enter,.support-panel-leave-to{opacity:0;transform:translateY(14px) scale(.97)}.support-teaser-enter-active,.support-teaser-leave-active{transition:opacity .2s ease,transform .2s ease}.support-teaser-enter,.support-teaser-leave-to{opacity:0;transform:translateY(8px)}@keyframes support-dot{0%,60%,100%{opacity:.35;transform:translateY(0)}30%{opacity:1;transform:translateY(-2px)}}@media(max-width:680px){.support-widget{right:12px;bottom:12px}.support-launcher{width:52px;height:52px}.support-panel{position:fixed;right:8px;bottom:72px;left:8px;width:auto;height:min(680px,calc(100dvh - 90px));border-radius:20px}.support-teaser{right:0;bottom:64px}.support-head{padding:13px}.support-messages{padding:14px 10px}.support-message>div{max-width:88%}}@media(max-width:380px){.support-panel{right:0;bottom:0;left:0;height:100dvh;border:0;border-radius:0}.support-launcher{width:50px;height:50px}.support-widget.is-open .support-launcher{display:none}}
.support-legal{display:flex;align-items:flex-start;gap:7px;padding:8px 12px;border-top:1px solid #eadfd5;background:#fff;color:#756a62;font-size:7.5px;line-height:1.45}.support-legal input{width:13px;height:13px;flex:0 0 auto;margin:0;accent-color:#b07c50}.support-legal button{padding:0;border:0;background:transparent;color:#8f603b;font:inherit;text-decoration:underline;cursor:pointer}
</style>
